import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SplitPane } from 'react-multi-split-pane';
import { container } from 'tsyringe';
import { debounce } from 'lodash';
import { useHistory } from 'react-router-dom';

import './Channel.scss';

import BackStage from '../../components/layout/BackStage';
import OnStage from '../../components/layout/OnStage';
import SideNav from '../../components/layout/SideNav';
import SidePanel from '../../components/layout/SidePanel';
import TopToolbar from '../../components/layout/TopToolbar';

import { ApiService } from '../../services/api-service';
import { AssetManagementService } from '../../services/asset-management-service';
import { CmsManagementService } from '../../services/cms-management-service';
import { UserService } from '../../services/user-service';
import { useSharedState } from '../../utils/helpers';
import { WebRTCsdkService } from '../../services/webrtcsdk-service';
import { PTApiService } from '../../services/ptapi-service';
import { SEAMApiService } from '../../services/seamapi-service';
import { OrchestratorService } from '../../services/orchestrator-service';
import { PollService } from '../../services/poll-service';
import { QnaService } from '../../services/qna-service';
import { PeopleService } from '../../services/people-service';
import { SoundKey, SoundService } from '../../services/sound-service';

import FakeRemoteVideo from '../../components/producerSelector/FakeRemoteVideo';

import WelcomeTuto from '../../components/welcome-tutos/WelcomeTuto';
import CoachMarks from '../../components/welcome-tutos/CoachMarks';
import DeviceErrorDialog from '../../components/layout/DeviceErrorDialog';
import { BroadcastService } from '../../services/broadcast-service';
import { PTSocketService } from '../../services/ptsocket.service';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { EventSubpage } from '../event/Event';
import RegisterPopup from '../../components/layout/RegisterPopup';

// import Lottie from 'react-lottie-player';
// import lottieLoadingStudioJson from '../../assets/lottie/loading-studio.json';
import '../event/Event.scss';


export const Channel = ({ channelId, goToPage }: { channelId: string, goToPage: (page: EventSubpage) => any }) => {

  const [orchestratorService] = useState(container.resolve(OrchestratorService));
  const [apiService] = useState(container.resolve(ApiService));
  const [webRTCsdkService] = useState(container.resolve(WebRTCsdkService));
  const [userService] = useState(container.resolve(UserService));
  const [ptApiService] = useState(container.resolve(PTApiService));
  const [seamApiService] = useState(container.resolve(SEAMApiService));
  const [pollService] = useState(container.resolve(PollService));
  const [qnaService] = useState(container.resolve(QnaService));
  const [peopleService] = useState(container.resolve(PeopleService));
  const [assetManagementService] = useState(container.resolve(AssetManagementService));
  const [cmsManagementService] = useState(container.resolve(CmsManagementService));
  const [soundService] = useState(container.resolve(SoundService));
  const [broadcastService] = useState(container.resolve(BroadcastService));
  const [ptSocketService] = useState(container.resolve(PTSocketService));

  const [channelList] = useSharedState(apiService.channelList$);
  const history = useHistory();
  const [meetingInitialized, setMeetingInitialized] = useState(false);

  const [devicePermissionError] = useSharedState(webRTCsdkService.devicePermissionError$);

  const tutorialCompleted = localStorage.getItem("tutorialCompleted");
  const [showTuto, setShowTuto] = useState({ welcome: tutorialCompleted && tutorialCompleted === 'true' ? false : true, coachmark: false });

  const [devicePermissionErrorVisible, setDevicePermissionErrorVisible] = useState(null)

  const [isLoadingEvent, setIsLoadingEvent] = useState(true);


  const closeDevicePermissionError = (value) => {
    setDevicePermissionErrorVisible(null);

    // do nothing : just hide dialog

    // if (value === 'close') {
    //   // do nothing : just hide dialog

    // } else if (value === 'cancel') {
    //   console.log('reload page');

    //   // reload page to login
    //   soundService.playSoundStart(SoundKey.Exit);
    //   location.reload();

    // } else {
    //   // route to haircheck page
    //   soundService.playSoundStart(SoundKey.Exit);
    //   goToPage(EventSubpage.HAIRCHECK);
    // }
  }

  const initialize = useCallback(async () => {
    const channel = await apiService.getChannelInfos('testing');

    // if (!channel?.meetingProperties) {
    //   throw new Error('Error channel meetingProperties not set, cannot join meeting');
    // }

    // const [publicEvent] = ptApiService.ptCurrentEventInformation$.value;

    // // The instanceId is not synced with the channel, leave the channel
    // if (!publicEvent.instanceId || publicEvent.instanceId !== channel.meetingProperties.ptEventInstanceId) {
    //   console.error(`The instanceId of the pt event is not synced with the channel. Channel has been left. pt event instanceId=${publicEvent.instanceId} channelInstanceId=${channel.meetingProperties.ptEventInstanceId}`);
    //   history.push('/select-channel');
    //   return;
    // }
  }, []);

  const onWelcomeAction = (action) => {
    setShowTuto({ welcome: false, coachmark: action === 'showTuto' });
  }

  const onCoachMarkAction = (action) => {
    setShowTuto({ welcome: false, coachmark: false });
  }

  useEffect(() => {
    const subs = [
      orchestratorService.meetingInitializedObservable.subscribe(async (init) => {
        setMeetingInitialized(init);
        await assetManagementService.loadAssets(false);  // for current user
        await assetManagementService.loadAssets(true);   // for meeting organizer
        cmsManagementService.getContentsVideo();
      }),
      pollService.bindToSocket(),
      qnaService.bindToSocket(),
    ];

    return () => subs.forEach(s => s.unsubscribe());
  }, []);

  useEffect(() => {
    if (devicePermissionError) {
      soundService.playSoundStart(SoundKey.Popup);
      setDevicePermissionErrorVisible(devicePermissionError);
    }
  }, [devicePermissionError])

  const onSplitResize = () => {
    // dispatch resize to udpate live video size
    window.dispatchEvent(new Event('resize'));
  }

  useEffect(() => {
    initialize();
    // force a resize after first display
    debounce(onSplitResize, 20)();
  }, []);

  useEffect(() => {
    const sub = new Subscription();

    sub.add(
      ptSocketService.eventState$.pipe(filter(s => !!s)).subscribe(s => broadcastService.onPrimetimeEventReceived(s))
    );

    sub.add(
      apiService.event$.pipe(filter(e => !!e)).subscribe(e => broadcastService.onStudioEventReceived(e))
    );

    return () => sub.unsubscribe();
  }, []);

  // TMP
  useEffect(() => {
    const sub = broadcastService.broadcastStateObservable.subscribe(s => console.log('BV Broadcast state changed', s));

    return () => sub.unsubscribe();
  }, []);

  return (
    <div className="app">
      <FakeRemoteVideo />
      <TopToolbar></TopToolbar>

      <div className="left-area" id="left-area">

        <SplitPane className="split-vertical" split="horizontal"
          minSize={[300, 210]}
          defaultSizes={[420, 180]}
          onChange={(e) => debounce(onSplitResize, 20)()}
        >
          <OnStage></OnStage>
          <BackStage></BackStage>
        </SplitPane>
      </div>

      <SidePanel></SidePanel>
      <SideNav></SideNav>


      {!devicePermissionErrorVisible && showTuto.welcome &&
        <WelcomeTuto onAction={onWelcomeAction}></WelcomeTuto>
      }
      {!devicePermissionErrorVisible && !showTuto.welcome && showTuto.coachmark &&
        <CoachMarks onAction={onCoachMarkAction}></CoachMarks>
      }
      {devicePermissionErrorVisible &&
        <DeviceErrorDialog open={devicePermissionErrorVisible !== null} onClose={(value) => closeDevicePermissionError(value)} ></DeviceErrorDialog>
      }
      <RegisterPopup></RegisterPopup>



    </div>
  )

}